import React, { useMemo, useState, useEffect } from 'react';
import { Bell, ChevronRight, Menu, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useLocation, Link } from 'react-router-dom';
import api from '../utils/api';

const Header = ({ onMenuToggle }) => {
  const { user } = useAuth();
  const { count: notificationCount, fetchCount } = useNotifications();
  const location = useLocation();
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Load profile photo
  useEffect(() => {
    const loadProfilePhoto = async () => {
      try {
        const response = await api.get('/profile');
        if (response.data?.foto_url) {
          setProfilePhoto(response.data.foto_url);
        }
      } catch (error) {
        console.error('Error loading profile photo:', error);
      }
    };
    
    if (user) {
      loadProfilePhoto();
    }
  }, [user]);

  // Polling para notificaciones cada 30 segundos
  useEffect(() => {
    if (user) {
      // Fetch inicial
      fetchCount();
      
      // Polling cada 30 segundos
      const interval = setInterval(fetchCount, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, fetchCount]);

  const breadcrumbs = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    const breadcrumbMap = {
      'dashboard': { label: 'Dashboard', parent: null },
      'solicitudes': { label: 'Requerimientos', parent: 'Postventa' },
      'configuracion': { label: 'Configuración', parent: 'Postventa' },
      'usuarios': { label: 'Usuarios', parent: 'Administración' },
      'roles': { label: 'Roles', parent: 'Administración' },
      'menus': { label: 'Menús', parent: 'Administración' },
      'configuracion-general': { label: 'General', parent: 'Administración' },
      'auditoria': { label: 'Auditoría', parent: 'Administración' },
      'perfil': { label: 'Mi Perfil', parent: null },
      'notificaciones': { label: 'Notificaciones', parent: null },
    };

    if (pathSegments.length === 0) return [];
    
    const currentPage = pathSegments[pathSegments.length - 1];
    const pageInfo = breadcrumbMap[currentPage];
    
    if (!pageInfo) return [];

    const crumbs = [];
    if (pageInfo.parent) {
      crumbs.push({ label: pageInfo.parent, path: null });
    }
    crumbs.push({ label: pageInfo.label, path: location.pathname });

    return crumbs;
  }, [location.pathname]);

  return (
    <header className="h-14 lg:h-16 bg-white border-b border-[#E8E8E7] flex items-center justify-between px-3 sm:px-4 lg:px-8" data-testid="header">
      <div className="flex items-center gap-2 lg:gap-4 min-w-0">
        {/* Mobile menu button */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 hover:bg-gray-100 rounded-md -ml-1"
          aria-label="Abrir menú"
        >
          <Menu size={22} className="text-[#5A5A5A]" />
        </button>

        {/* Title - hidden on small mobile */}
        <h2 className="hidden sm:block text-base lg:text-xl font-semibold text-[#2D2D2D] truncate">
          Sistema de Postventa
        </h2>
        
        {/* Mobile: Show current page */}
        <h2 className="sm:hidden text-base font-semibold text-[#2D2D2D] truncate">
          {breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : 'Postventa'}
        </h2>
        
        {/* Breadcrumbs - hidden on mobile */}
        {breadcrumbs.length > 0 && (
          <div className="hidden md:flex items-center gap-2 text-sm">
            <ChevronRight size={16} className="text-[#888888]" />
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <ChevronRight size={14} className="text-[#888888]" />}
                {crumb.path ? (
                  <Link 
                    to={crumb.path} 
                    className="text-[#9d8272] hover:text-[#8a7162] font-medium"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-[#888888]">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 lg:gap-4">
        <Link 
          to="/notificaciones" 
          className="relative p-1.5 lg:p-2 hover:bg-[#F5F5F5] rounded-md transition-colors" 
          data-testid="notifications-button"
        >
          <Bell size={18} className="lg:w-5 lg:h-5 text-[#5A5A5A]" />
          {notificationCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </Link>
        
        {/* Profile Avatar - Link to profile page */}
        <Link 
          to="/perfil" 
          className="h-8 w-8 lg:h-10 lg:w-10 rounded-full overflow-hidden border-2 border-[#c5b8a2] hover:border-[#9d8272] transition-colors cursor-pointer"
          title="Mi Perfil"
        >
          {profilePhoto ? (
            <img 
              src={profilePhoto} 
              alt="Foto de perfil"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-full h-full bg-[#c5b8a2] flex items-center justify-center text-white font-semibold text-sm lg:text-base"
            style={{ display: profilePhoto ? 'none' : 'flex' }}
          >
            {user?.nombre?.charAt(0) || <User size={18} />}
          </div>
        </Link>
      </div>
    </header>
  );
};

export default Header;
